import { useState } from 'react';
import type { FC } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card/Card';
import { useTranslation } from '@/hooks/useTranslation';
import type { MLCuttingParams } from '../type/MLTypes';

interface MLCuttingParamsStepProps {
  params: MLCuttingParams;
  isFromProcess: boolean;
  isUserEdited: boolean;
  onParamChange: (key: keyof MLCuttingParams, value: number) => void;
  onResetToDefaults: () => void;
  disabled: boolean;
}

interface ParamConfig {
  key: keyof MLCuttingParams;
  sym: string;
  step: number;
}

const PARAM_CONFIGS: ParamConfig[] = [
  { key: 'fz', sym: 'fz', step: 0.001 },
  { key: 'ap', sym: 'ap', step: 0.1 },
  { key: 'ae', sym: 'ae', step: 0.1 },
  { key: 'vc', sym: 'vc', step: 1 },
  { key: 'nbDents', sym: 'z', step: 1 },
  { key: 'diameterCut', sym: 'Dc', step: 1 },
];

// Only these four fields can be sourced from process data; nbDents/diameterCut always come from defaults.
const PROCESS_SOURCED_FIELDS = new Set<keyof MLCuttingParams>(['fz', 'ap', 'ae', 'vc']);

interface MLCuttingParamRowProps {
  config: ParamConfig;
  value: number;
  isFromProcess: boolean;
  isUserEdited: boolean;
  disabled: boolean;
  onChange: (key: keyof MLCuttingParams, value: number) => void;
}

const MLCuttingParamRow: FC<MLCuttingParamRowProps> = ({
  config,
  value,
  isFromProcess,
  isUserEdited,
  disabled,
  onChange,
}) => {
  const { t } = useTranslation();
  const showPreFilled = isFromProcess && !isUserEdited;

  return (
    <div className="grid grid-cols-3 items-center gap-2">
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center rounded px-1.5 py-0.5 bg-primary/10 text-primary font-mono text-2xs font-bold">
          {config.sym}
        </span>
        <span className="text-xs text-muted-foreground">
          {t(`mlRul.context.params.${config.key}`)}
        </span>
      </div>
      <div className="flex items-center gap-1">
        <input
          data-testid={`input-${config.key}`}
          type="number"
          step={config.step}
          value={value}
          disabled={disabled}
          onChange={(e) => {
            const parsed = parseFloat(e.target.value);
            if (!isNaN(parsed)) {
              onChange(config.key, parsed);
            }
          }}
          className="w-full rounded border border-border bg-background px-2 py-1 text-xs text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
        />
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {t(`mlRul.context.params.units.${config.key}`)}
        </span>
      </div>
      <div className="flex justify-end">
        {showPreFilled && (
          <span
            data-testid="prefilled-label"
            className="text-2xs text-muted-foreground italic"
          >
            {t('mlRul.context.step4PreFilled')}
          </span>
        )}
      </div>
    </div>
  );
};

export const MLCuttingParamsStep: FC<MLCuttingParamsStepProps> = ({
  params,
  isFromProcess,
  isUserEdited,
  onParamChange,
  onResetToDefaults,
  disabled,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const badgeLabel = isUserEdited
    ? t('mlRul.context.step4BadgeCustom')
    : isFromProcess
      ? t('mlRul.context.step4BadgeFromProcess')
      : t('mlRul.context.step4BadgeDefaults');

  const headerClass = isExpanded
    ? 'block w-full rounded-xl border border-primary/40 bg-primary/5 p-3 text-left cursor-pointer outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50'
    : 'block w-full rounded-xl border border-border bg-card/60 p-3 text-left cursor-pointer outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50';

  return (
    <Card tier="inset" padding="none">
      <button
        type="button"
        data-testid="cutting-params-header"
        className={headerClass}
        onClick={() => setIsExpanded((v) => !v)}
        aria-expanded={isExpanded}
      >
        <span className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <span
              className={
                isExpanded
                  ? 'inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground text-2xs font-bold shrink-0'
                  : 'inline-flex h-5 w-5 items-center justify-center rounded-full border border-border text-2xs font-bold text-muted-foreground shrink-0'
              }
            >
              5
            </span>
            <span className="text-sm font-medium text-foreground">
              {t('mlRul.context.step4Label')}
            </span>
            <Badge size="sm" caps data-testid="params-badge">
              {badgeLabel}
            </Badge>
          </span>
          <span className="text-muted-foreground">
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" aria-hidden />
            ) : (
              <ChevronDown className="h-4 w-4" aria-hidden />
            )}
          </span>
        </span>

        {!isExpanded && (
          <span className="mt-2 flex flex-wrap gap-1">
            {PARAM_CONFIGS.map(({ key, sym }) => (
              <span
                key={key}
                data-testid={`chip-${key}`}
                className="inline-flex items-center gap-1 rounded border border-border/60 bg-muted/40 px-1.5 py-0.5 text-2xs text-foreground"
              >
                <span className="font-mono font-bold text-primary">{sym}</span>
                <span>{params[key]}</span>
                <span className="text-muted-foreground">{t(`mlRul.context.params.units.${key}`)}</span>
              </span>
            ))}
          </span>
        )}
      </button>

      {isExpanded && (
        <div className="p-3 flex flex-col gap-3">
          {PARAM_CONFIGS.map((config) => (
            <MLCuttingParamRow
              key={config.key}
              config={config}
              value={params[config.key]}
              isFromProcess={isFromProcess && PROCESS_SOURCED_FIELDS.has(config.key)}
              isUserEdited={isUserEdited}
              disabled={disabled}
              onChange={onParamChange}
            />
          ))}
          <div className="flex justify-end">
            <Button
              data-testid="reset-defaults-button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                onResetToDefaults();
              }}
            >
              {t('mlRul.context.step4ResetToDefaults')}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};
